import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const SOURCE_LABELS = {
    navbar: "Navbar (phone pill)",
    "navbar-mobile": "Mobile menu",
    hero: "Hero — Call Now",
    "mobile-bar": "Mobile sticky bar",
    footer: "Footer",
    "final-cta": "Final CTA section",
    location: "Location section",
    "demo-booking": "Demo booking section",
    "whatsapp-widget": "WhatsApp chat widget",
};

const fmtDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

export default function AnalyticsTab() {
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/api/admin/analytics");
            setData(res.data);
        } catch (e) {
            toast.error(apiError(e, "Failed to load analytics"));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (!data) {
        return (
            <div className="flex justify-center py-20" data-testid="analytics-loading">
                <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-blue" />
            </div>
        );
    }

    const { totals, sources, daily } = data;
    const maxSource = Math.max(1, ...sources.map((s) => s.count));
    const maxDaily = Math.max(1, ...daily.map((d) => d.call + d.whatsapp));

    return (
        <div data-testid="analytics-tab" className="space-y-6">
            {/* Totals */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div data-testid="analytics-call-total" className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4">
                    <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-ink/45">
                        <Phone size={13} className="text-brand-blue" /> Call Clicks
                    </p>
                    <p className="mt-1 text-3xl font-bold tracking-tight text-ink">{totals.call}</p>
                </div>
                <div data-testid="analytics-whatsapp-total" className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4">
                    <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-ink/45">
                        <MessageCircle size={13} className="text-brand-teal" /> WhatsApp Clicks
                    </p>
                    <p className="mt-1 text-3xl font-bold tracking-tight text-ink">{totals.whatsapp}</p>
                </div>
                <div data-testid="analytics-today-total" className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/45">Clicks Today</p>
                    <p className="mt-1 text-3xl font-bold tracking-tight text-ink">{totals.today}</p>
                </div>
            </div>

            {/* By source */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-6" data-testid="analytics-sources">
                <h3 className="text-base font-bold tracking-tight text-ink">Clicks by Source</h3>
                <p className="mt-0.5 text-xs font-medium text-ink/45">Kaunse button se kitne log call / WhatsApp kar rahe hain</p>
                {sources.length === 0 ? (
                    <p className="mt-5 text-sm font-medium text-ink/45" data-testid="analytics-empty">
                        Abhi tak koi click record nahi hua. Jaise hi visitors Call ya WhatsApp buttons dabayenge, yahan data dikhega.
                    </p>
                ) : (
                    <div className="mt-5 space-y-3">
                        {sources.map((s) => (
                            <div key={`${s.event}-${s.source}`} data-testid={`source-row-${s.source}-${s.event}`}>
                                <div className="flex items-center justify-between text-[13px] font-semibold">
                                    <span className="text-ink/75">
                                        {SOURCE_LABELS[s.source] || s.source}
                                        <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${s.event === "call" ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-600"}`}>
                                            {s.event === "call" ? "Call" : "WhatsApp"}
                                        </span>
                                    </span>
                                    <span className="text-ink">{s.count}</span>
                                </div>
                                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                                    <div
                                        className={`h-full rounded-full ${s.event === "call" ? "bg-gradient-to-r from-brand-purple to-brand-blue" : "bg-gradient-to-r from-brand-blue to-brand-teal"}`}
                                        style={{ width: `${(s.count / maxSource) * 100}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Last 14 days */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-6" data-testid="analytics-daily">
                <h3 className="text-base font-bold tracking-tight text-ink">Last 14 Days</h3>
                <div className="mt-5 flex h-36 items-end gap-1.5">
                    {daily.map((d) => (
                        <div key={d.date} className="group relative flex h-full flex-1 flex-col items-center justify-end gap-0.5" title={`${fmtDay(d.date)} — Call: ${d.call}, WhatsApp: ${d.whatsapp}`}>
                            <div className="w-full rounded-t bg-gradient-to-t from-brand-purple to-brand-blue" style={{ height: `${(d.call / maxDaily) * 100}%`, minHeight: d.call ? 3 : 0 }} />
                            <div className="w-full rounded-t bg-gradient-to-t from-brand-blue to-brand-teal" style={{ height: `${(d.whatsapp / maxDaily) * 100}%`, minHeight: d.whatsapp ? 3 : 0 }} />
                        </div>
                    ))}
                </div>
                <div className="mt-2 flex gap-1.5">
                    {daily.map((d) => (
                        <div key={d.date} className="flex-1 text-center text-[9px] font-semibold text-ink/35">
                            {fmtDay(d.date)}
                        </div>
                    ))}
                </div>
                <div className="mt-4 flex items-center gap-5 text-[11px] font-bold text-ink/50">
                    <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-brand-blue" /> Call</span>
                    <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-brand-teal" /> WhatsApp</span>
                </div>
            </div>
        </div>
    );
}
